import { useWidgetContext } from '@ptek/widget-core';
import { useCallback, useContext } from 'react';
import { reportMalformedHostPayload } from '../config/reportMalformedHostPayload';
import { AppContext } from '../context/AppContext';
import { useRecordingStore } from '../stores';
import { Patient } from '../types/appointment';
import {
  AmbientScribeContext,
  ContextData,
  ContextDataItem,
} from '../types/widget';
import {
  ContextSelectionState,
  UserSettingsPreferences,
} from '../utils/userPreferences';

/**
 * Calculates age from a date of birth string
 */
function calculateAge(dateOfBirth: string): number | null {
  const birthDate = new Date(dateOfBirth);
  if (isNaN(birthDate.getTime())) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
}

/**
 * Formats patient demographics into a readable string
 */
function formatPatientDemographics(patient: Patient): string {
  const parts: string[] = [];

  const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ');
  if (name) {
    parts.push(`Patient name: ${name}`);
  }

  if (patient.dateOfBirth) {
    const age = calculateAge(patient.dateOfBirth);
    if (age !== null) {
      parts.push(`Age: ${age}`);
    }
  }

  if (patient.gender) {
    parts.push(`Gender: ${patient.gender}`);
  }

  return parts.join('. ') + '.';
}

function withPatientDemographics(
  data: ContextData,
  patient: Patient | undefined
): ContextData {
  if (
    patient &&
    (patient.firstName ||
      patient.lastName ||
      patient.dateOfBirth ||
      patient.gender) &&
    !data.some((item) => item.key === 'patientDemographics')
  ) {
    const patientDemographicsItem: ContextDataItem = {
      key: 'patientDemographics',
      value: formatPatientDemographics(patient),
      hidden: true,
    };
    return [patientDemographicsItem, ...data];
  }
  return data;
}

export const useContextDataHandler = (
  userId?: string,
  loadPreferences?: (userId: string) => UserSettingsPreferences
) => {
  const { updateSessionModel } = useContext(AppContext);
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();

  const patient = widgetContext.context?.appointment?.patient;

  /**
   * Normalizes context data by setting default selected state.
   * Items with persistPreviousSelection: true will use saved localStorage state if available,
   * otherwise default to selected (true).
   * Items without persistPreviousSelection default to unselected (false).
   */
  const normalizeContextData = useCallback(
    (data: ContextData): ContextData => {
      const savedSelections: Record<string, ContextSelectionState> =
        (userId && loadPreferences
          ? loadPreferences(userId).lastContextSelections
          : {}) || {};

      return data.map((item) => {
        const savedState = item.persistPreviousSelection
          ? savedSelections[String(item.key)]
          : undefined;

        if (item.children && item.children.length > 0) {
          const normalizedChildren = item.children.map((child) => {
            if (item.persistPreviousSelection) {
              if (savedState) {
                return {
                  ...child,
                  selected: savedState[String(child.key)] === true,
                };
              }
              return {
                ...child,
                selected: true,
              };
            }
            return {
              ...child,
              selected: false,
            };
          });

          // Parent is selected if any child is selected
          const anyChildSelected = normalizedChildren.some((c) => c.selected);

          return {
            ...item,
            selected: anyChildSelected,
            children: normalizedChildren,
          };
        }

        if (item.persistPreviousSelection) {
          if (savedState) {
            return {
              ...item,
              selected: savedState.selected === true,
            };
          }
          return {
            ...item,
            selected: true,
          };
        }

        return {
          ...item,
          selected: false,
        };
      });
    },
    [userId, loadPreferences]
  );

  const mergeContextData = useCallback(
    (existing: ContextData, updates: ContextData): ContextDataItem[] => {
      const merged = existing.map((existingItem): ContextDataItem => {
        const update = updates.find((u) => u.key === existingItem.key);

        if (!update) {
          return existingItem;
        }

        let mergedChildren = existingItem.children;
        if (existingItem.children && update.children) {
          mergedChildren = existingItem.children.map((existingChild) => {
            const childUpdate = update.children?.find(
              (c) => c.key === existingChild.key
            );
            return childUpdate
              ? {
                  ...existingChild,
                  value: childUpdate.value,
                  selected: childUpdate.selected,
                }
              : existingChild;
          });

          const newChildren = update.children.filter(
            (uc) => !existingItem.children?.some((c) => c.key === uc.key)
          );
          if (newChildren.length > 0) {
            mergedChildren = [...mergedChildren, ...newChildren];
          }
        }

        return {
          ...existingItem,
          value: update.value,
          children: mergedChildren,
        };
      });

      const newItems = updates.filter(
        (update) => !existing.some((e) => e.key === update.key)
      );

      return [...merged, ...newItems];
    },
    []
  );

  const handleSetContextData = useCallback(
    (data: ContextData) => {
      if (!Array.isArray(data)) {
        reportMalformedHostPayload(
          'useContextDataHandler',
          'Ignoring malformed setContextData payload (expected an array):',
          data
        );
        return;
      }

      // A patient switch is parked while the open take stops, so this context
      // belongs to the incoming encounter; hold it for the switch to apply.
      const { pendingEncounter, pendingContextData, setPendingContextData } =
        useRecordingStore.getState();
      if (pendingEncounter) {
        const normalizedData = normalizeContextData(
          withPatientDemographics(data, pendingEncounter.patient)
        );
        setPendingContextData({
          appointmentId: pendingEncounter.id,
          contextData:
            pendingContextData?.appointmentId === pendingEncounter.id
              ? mergeContextData(pendingContextData.contextData, normalizedData)
              : normalizedData,
        });
        return;
      }

      updateSessionModel((prev) => {
        // Always normalize incoming data to apply localStorage selections
        const normalizedData = normalizeContextData(
          withPatientDemographics(data, patient)
        );

        if (prev.contextData.length > 0) {
          return {
            ...prev,
            contextData: mergeContextData(prev.contextData, normalizedData),
          };
        }

        return {
          ...prev,
          contextData: normalizedData,
        };
      });
    },
    [updateSessionModel, normalizeContextData, mergeContextData, patient]
  );

  return { handleSetContextData };
};
